/**
 * ウェビナー動画の YouTube 埋め込み中継ページ（public/youtube_player.html）との接続部。
 *
 * アプリ（Capacitor）の WebView は capacitor:// スキームで動くため、そこから直接 YouTube を
 * 埋め込むと Referer が送られず、YouTube 側の埋め込み元識別に失敗して再生が拒否される
 * （エラーコード 153 など）。そのため http(s) オリジンに置いた中継ページを iframe で挟み、
 * YouTube から見た埋め込み元が自社ドメインになるようにする。
 *
 * メッセージのプロトコルは public/youtube_player.html と対になっているため、片方だけを変更しないこと。
 */

/** 中継ページの配置パス（vite の public 配下 → ビルド成果物の直下） */
export const YOUTUBE_PLAYER_PAGE_PATH = "/youtube_player.html";

/**
 * ページ自身のオリジンが http(s) でない（＝ capacitor:// で動くアプリの WebView）ときに使う、
 * 中継ページの配信元。環境ごとの Web 配信ドメインを指す。
 */
const REMOTE_PLAYER_PAGE_ORIGINS: Record<string, string> = {
  production: "https://propally.jp",
  test: "https://prpldev.work",
  development: "https://prpldev.work",
};

const DEFAULT_REMOTE_PLAYER_PAGE_ORIGIN = REMOTE_PLAYER_PAGE_ORIGINS.test;

/** 動画IDの形式（API 側の Webinar::YOUTUBE_VIDEO_ID_FORMAT と同じ） */
const YOUTUBE_VIDEO_ID_FORMAT = /^[A-Za-z0-9_-]{11}$/;

export const isValidYoutubeVideoId = (videoId: string | undefined | null): boolean =>
  typeof videoId === "string" && YOUTUBE_VIDEO_ID_FORMAT.test(videoId);

/**
 * 中継ページの配信元オリジンを決める。
 * - http(s) オリジン（ブラウザ版・Android の WebView）: 自分自身のオリジン。
 *   バンドルに同梱された中継ページがそのまま使え、Referer も送られる
 * - それ以外（iOS の capacitor:// など）: Referer が送られないため、環境ごとの Web 配信ドメインを使う
 */
export const resolveYoutubePlayerPageOrigin = (): string => {
  const { protocol, origin } = window.location;
  if (protocol === "https:" || protocol === "http:") {
    return origin;
  }
  return REMOTE_PLAYER_PAGE_ORIGINS[process.env.NODE_ENV] ?? DEFAULT_REMOTE_PLAYER_PAGE_ORIGIN;
};

/** 中継ページの URL。動画IDの形式が不正な場合は null を返す */
export const buildYoutubePlayerPageUrl = (videoId: string): string | null => {
  if (!isValidYoutubeVideoId(videoId)) {
    return null;
  }
  return `${resolveYoutubePlayerPageOrigin()}${YOUTUBE_PLAYER_PAGE_PATH}?v=${encodeURIComponent(videoId)}`;
};

/** YT.PlayerState と同じ値（中継ページから受け取った state の判定に使う） */
export const YOUTUBE_PLAYER_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

/** 中継ページが送ってくるメッセージの識別子（public/youtube_player.html の MESSAGE_SOURCE と一致させる） */
export const YOUTUBE_PLAYER_PAGE_MESSAGE_SOURCE = "propally-webinar-youtube";

/**
 * 中継ページ → アプリのメッセージ。
 * - ready: プレイヤーの初期化完了
 * - state: 再生状態の変化（ENDED のときだけ duration が付く）
 * - progress: 再生中の現在位置通知（中継ページ側で 1 秒間隔）
 * - error: YT.Player の onError（コードはそのまま）
 * - load_error: IFrame API のロード失敗（通信不良・広告ブロッカー等）
 */
export type YoutubePlayerPageMessage =
  | { type: "ready" }
  | { type: "state"; state: number; currentTime: number; duration?: number }
  | { type: "progress"; currentTime: number }
  | { type: "error"; code?: number }
  | { type: "load_error" };

const MESSAGE_TYPES: readonly string[] = ["ready", "state", "progress", "error", "load_error"];

/** 受信した MessageEvent の data が中継ページからのメッセージかを判定する */
export const parseYoutubePlayerPageMessage = (data: unknown): YoutubePlayerPageMessage | null => {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  const message = data as { source?: unknown; type?: unknown };
  if (message.source !== YOUTUBE_PLAYER_PAGE_MESSAGE_SOURCE) {
    return null;
  }
  if (typeof message.type !== "string" || !MESSAGE_TYPES.includes(message.type)) {
    return null;
  }
  return message as YoutubePlayerPageMessage;
};

/**
 * 中継ページの読み込み後、ready / state / error のいずれも来ない場合に
 * iframe 遮断（広告ブロッカー・企業ネットワーク等）とみなす watchdog のタイムアウト（ms）。
 * 中継ページ側の IFrame API ロードタイムアウト（10 秒）より長くして、
 * 中継ページ自身が load_error を返す余地を残す
 */
export const YOUTUBE_PLAYER_INIT_TIMEOUT_MS = 15000;

/**
 * 再試行しても回復しない YT エラーコード。
 * 100: 動画が見つからない（削除・非公開） / 101・150: 埋め込み禁止
 * 152・153: 埋め込み元（Referer）を YouTube が識別できない。中継ページを挟んでも解消しない場合は
 *           再試行しても回復しないため、YouTube 本体への導線を出す
 */
const NON_RECOVERABLE_YOUTUBE_ERROR_CODES: readonly number[] = [100, 101, 150, 152, 153];

/**
 * YT.Player の onError コード（https://developers.google.com/youtube/iframe_api_reference#onError）が
 * 再試行で回復し得るかを判定する。
 * 上記の非回復コード以外（2: パラメータ不正 / 5: HTML5 プレイヤーエラー / 不明コード / コード無し）は
 * WebView 依存で一時的に発生し得るため、load 失敗と同じ回復可能扱い（再試行導線）にする
 */
export const isRecoverableYoutubeError = (code?: number): boolean =>
  code === undefined || !NON_RECOVERABLE_YOUTUBE_ERROR_CODES.includes(code);
